import { Flex, Tag, IconArrowDownSolid, Text } from "@smarterservices/smarter-ui";
import React, { useImperativeHandle, forwardRef } from "react";
import useChat from "./useChat";
import MessageList from "./MessageList";
import ChatInput from "./ChatInput";

/**
 * ReactChat Component
 * 
 * A comprehensive chat interface for proctoring sessions with real-time messaging,
 * message history, and session time tracking. This component integrates with the
 * MediaAgent for WebSocket-based real-time updates and supports synchronization
 * with video/audio playback.
 * 
 * ## Features
 * - Real-time messaging via WebSocket (MediaAgent)
 * - Message history with timestamps
 * - Session time tracking (relative to session start)
 * - Scroll to specific session times
 * - Message highlighting for playback synchronization
 * - Optional message input (can be disabled for read-only mode)
 * - Custom session time rendering
 * - Connection bundle support for simplified configuration
 *
 * @component
 * @param {object} props - Component properties
 * 
 * @param {string} [props.roomId] - Unique ID of the chat room (required if not using connectionBundle)
 * @param {string} [props.identity] - User's unique identity (required if not using connectionBundle)
 * @param {string} [props.token] - Auth token for API requests (required if not using connectionBundle)
 * @param {string} [props.apiUrl] - Base URL for API endpoints (required if not using connectionBundle)
 * @param {string} [props.websocketUrl] - WebSocket URL for real-time events (required if not using connectionBundle)
 * @param {string} props.referenceId - Reference ID included with messages
 * @param {string} props.displayName - User's display name shown in messages
 * 
 * @param {object} [props.connectionBundle] - Optional connection bundle containing roomId, identity, token, apiUrl, and websocketUrl. Use this instead of individual connection parameters.
 * @param {object} [props.mediaAgent] - Optional existing MediaAgent instance. If provided, it will be used instead of creating a new one. This is the recommended approach when sharing a MediaAgent across components.
 * @param {EventEmitter} [props.roomEventEmitter] - Optional existing MediaAgent event emitter (deprecated, use mediaAgent instead). If provided, a new MediaAgent instance will not be created.
 * @param {boolean} [props.allowNewMessage=true] - Whether to show the message input field. Set to false for read-only chat.
 * @param {Date|string|number} [props.startTime] - Session start time used to calculate relative session times for messages. Can be Date object, ISO string, or timestamp.
 * @param {function} [props.renderSessionTime] - Custom renderer for session time display. Receives (sessionTime: number, message: object) and returns a string or JSX.
 * @param {function} [props.onReady] - Callback fired when chat is fully initialized with messages loaded and connections established. Receives object with messageCount and connected status.
 * @param {function} [props.onNewMessage] - Callback fired when a new message is received. Receives the new message object.
 * 
 * @param {React.Ref} ref - Forwarded ref exposing imperative methods for controlling the chat
 * 
 * @returns {JSX.Element} The chat UI component
 * 
 * @example
 * // Basic usage with individual parameters
 * import { ReactChat } from '@shared/components/proctoring/ReactChat';
 * 
 * <ReactChat
 *   roomId="room-123"
 *   identity="user-456"
 *   token="auth-token"
 *   referenceId="session-789"
 *   apiUrl="https://api.example.com"
 *   websocketUrl="wss://ws.example.com"
 *   displayName="John Doe"
 * />
 * 
 * @example
 * // With session time tracking and custom time rendering
 * import { ReactChat } from '@shared/components/proctoring/ReactChat';
 * import { useRef } from 'react';
 * 
 * const chatRef = useRef(null);
 * 
 * <ReactChat
 *   ref={chatRef}
 *   roomId="room-123"
 *   identity="user-456"
 *   token="auth-token"
 *   referenceId="session-789"
 *   apiUrl="https://api.example.com"
 *   websocketUrl="wss://ws.example.com"
 *   displayName="John Doe"
 *   startTime={new Date('2025-04-29T04:00:00')}
 *   renderSessionTime={(seconds) => {
 *     const mins = Math.floor(seconds / 60);
 *     const secs = seconds % 60;
 *     return `${mins}:${secs.toString().padStart(2, '0')}`;
 *   }}
 * />
 * 
 * // Scroll to a specific session time (e.g., when video seeks to 2:00)
 * chatRef.current.scrollToMessageBySessionTime(120);
 * 
 * @example
 * // Using connectionBundle (recommended for SessionViewer integration)
 * import { ReactChat } from '@shared/components/proctoring/ReactChat';
 * 
 * const connectionBundle = {
 *   roomId: "room-123",
 *   identity: "user-456",
 *   token: "auth-token",
 *   apiUrl: "https://api.example.com",
 *   websocketUrl: "wss://ws.example.com"
 * };
 * 
 * <ReactChat
 *   connectionBundle={connectionBundle}
 *   referenceId="session-789"
 *   displayName="John Doe"
 *   allowNewMessage={false}
 * />
 * 
 * @example
 * // Using existing MediaAgent instance (recommended)
 * import { ReactChat } from '@shared/components/proctoring/ReactChat';
 * import { MediaAgent } from '@smarterservices/media-agent';
 * 
 * // Assume you have an existing MediaAgent instance
 * const existingMediaAgent = await MediaAgent({
 *   apiUrl: "https://api.example.com",
 *   roomId: "room-123",
 *   identity: "user-456",
 *   token: "auth-token",
 *   websocketUrl: "wss://ws.example.com"
 * });
 * await existingMediaAgent.joinRoom();
 * 
 * <ReactChat
 *   mediaAgent={existingMediaAgent}
 *   referenceId="session-789"
 *   displayName="John Doe"
 *   allowNewMessage={true}
 * />
 * 
 * @example
 * // Read-only chat with existing event emitter (legacy)
 * import { ReactChat } from '@shared/components/proctoring/ReactChat';
 * 
 * <ReactChat
 *   roomId="room-123"
 *   identity="user-456"
 *   token="auth-token"
 *   referenceId="session-789"
 *   apiUrl="https://api.example.com"
 *   websocketUrl="wss://ws.example.com"
 *   displayName="John Doe"
 *   roomEventEmitter={existingEmitter}
 *   allowNewMessage={false}
 * />
 */
const Chat = forwardRef((props, ref) => {
    // Ensure allowNewMessage has a default value
    if (!props.hasOwnProperty('allowNewMessage')) {
        props = {
            ...props,
            allowNewMessage: true
        }
    }

    // Initialize chat hook with all props
    const {
        messages,
        messageText,
        setMessageText,
        sendError,
        isSubmitting,
        showNewMessageButton,
        scrollToBottom,
        scrollToMessageBySessionTime,
        handleScroll,
        formatTimestamp,
        handleSubmit,
        chatContainerRef,
        messagesEndRef,
        messageRefs,
        highlightedMessageId,
        setHighlightedMessageId,
        identity,
        calculateSessionTime
    } = useChat(props);

    /**
     * Expose imperative methods to parent component via ref
     * 
     * These methods allow programmatic control of the chat for synchronization
     * with video playback, manual scrolling, and accessing chat state.
     * 
     * @typedef {object} ReactChatRef
     * @property {function} scrollToBottom - Scroll to the bottom of the chat
     * @property {function} scrollToMessageBySessionTime - Scroll to a message at a specific session time
     * @property {function} clearHighlightedMessage - Clear any highlighted message
     * @property {function} getMessages - Get the current array of messages
     * @property {function} calculateSessionTime - Calculate session time for a timestamp
     */
    useImperativeHandle(ref, () => ({
        /**
         * Scrolls the chat to the bottom
         * 
         * Useful for ensuring the latest messages are visible or resetting
         * the scroll position after user interaction.
         * 
         * @param {boolean} [instant=false] - If true, scrolls instantly without animation. If false, uses smooth scrolling.
         * @example
         * chatRef.current.scrollToBottom(true); // Instant scroll
         * chatRef.current.scrollToBottom(false); // Smooth scroll
         */
        scrollToBottom: (instant) => scrollToBottom(instant),

        /**
         * Scrolls to a message at a specific session time
         * 
         * This method is designed for synchronizing chat with video/audio playback.
         * It finds and scrolls to the message closest to the given session time,
         * highlighting it for visual feedback.
         * 
         * @param {number} sessionTime - Session time in seconds (relative to startTime)
         * @returns {boolean} True if an exact match was found, false if using closest match or no match
         * @example
         * // When video seeks to 2 minutes 30 seconds
         * const foundExact = chatRef.current.scrollToMessageBySessionTime(150);
         * if (foundExact) {
         *   console.log('Found message at exact time');
         * }
         */
        scrollToMessageBySessionTime,

        /**
         * Clears any currently highlighted message
         * 
         * Removes the visual highlight from messages. Useful when stopping
         * video playback or resetting the chat state.
         * 
         * @example
         * chatRef.current.clearHighlightedMessage();
         */
        clearHighlightedMessage: () => setHighlightedMessageId(null),

        /**
         * Gets the current array of messages
         * 
         * Returns all messages currently loaded in the chat, including
         * their session times if startTime was provided.
         * 
         * @returns {Array<object>} Array of message objects with properties:
         *   - sid: Message unique identifier
         *   - message: Message text content
         *   - identity: Sender's identity
         *   - displayName: Sender's display name
         *   - createdDate: Message timestamp
         *   - sessionTime: Relative session time in seconds (if startTime provided)
         * @example
         * const messages = chatRef.current.getMessages();
         * console.log(`Chat has ${messages.length} messages`);
         */
        getMessages: () => messages,

        /**
         * Calculates session time for a given timestamp
         * 
         * Converts an absolute timestamp to a relative session time based
         * on the startTime prop. Returns null if no startTime was provided.
         * 
         * @param {number} timestamp - Absolute timestamp in milliseconds since epoch
         * @returns {number|null} Session time in seconds, or null if startTime not set
         * @example
         * const sessionTime = chatRef.current.calculateSessionTime(Date.now());
         * console.log(`Current session time: ${sessionTime}s`);
         */
        calculateSessionTime
    }));

    // Render chat UI with two-section layout: scrollable messages and fixed input
    return (
        <Flex direction="column" width="100%" height="100%" as="div" position="relative">
            {/* Scrollable message list container */}
            <Flex.Item
                as="div"
                className="react-chat-messages-container"
                overflowY="auto"
                shouldGrow
                padding="x-small"
                margin="none"
                ref={chatContainerRef}
                onScroll={handleScroll}
                height="calc(100% - 90px)" // Reserve space for input at bottom
                position="relative"
            >
                <MessageList
                    messages={messages}
                    messagesEndRef={messagesEndRef}
                    formatTimestamp={formatTimestamp}
                    identity={identity}
                    messageRefs={messageRefs}
                    highlightedMessageId={highlightedMessageId}
                    renderSessionTime={props.renderSessionTime}
                />

            </Flex.Item>

            {/* Fixed message input at bottom (conditionally rendered) */}
            {props?.allowNewMessage && (
                <Flex.Item
                    style={{
                        height: '90px',
                        flexShrink: 0,
                        position: 'sticky',
                        bottom: 0,
                        background: 'white'
                    }}
                >
                    {showNewMessageButton && (
                        <div style={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 10,
                            marginTop: '-5px'
                        }}>
                            <Tag onClick={scrollToBottom} size="medium" text={<><Text color="danger">New Message<IconArrowDownSolid /> </Text></>} />
                        </div>
                    )}
                    <ChatInput
                        messageText={messageText}
                        setMessageText={setMessageText}
                        sendError={sendError}
                        isSubmitting={isSubmitting}
                        handleSubmit={handleSubmit}
                    />
                </Flex.Item>
            )}
        </Flex>
    );
});

// Display name for React DevTools debugging
Chat.displayName = 'ReactChat';

export default Chat;
